// SCR-STAFF-873 (AP-INV-02؛ US-INV-022): ظرف‌های باز چندمصرفی — مانده به واحد مصرف، مهلت مصرف پس از باز شدن (BR-INV-024) با شمارش معکوس،
// باز کردن ظرف جدید (FEFO یا بچ انتخابی) و دور ریختن با علت. ظرف منقضی را Job ساعتی به ضایعات می‌برد (AC-INV-022.3).
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type InvOpenContainerView, type InvWasteReason } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, ListPage, SampleBadge, StatusChip, faDigits, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { milliIn, stockKeys, useItemOptions, useWarehouses, WarehouseSelect, warehouseName, WASTE_REASONS } from './stockCommon';

export const SCR_STAFF_873 = 'SCR-STAFF-873';

/** ساعت باقی‌مانده تا پایان مهلت مصرف (منفی ← گذشته). */
export function hoursLeft(beyondUseAt: string, now: Date = new Date()): number {
  return Math.floor((new Date(beyondUseAt).getTime() - now.getTime()) / 3_600_000);
}

export function OpenContainersPage() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const warehouses = useWarehouses();
  const write = useWriteAccess('inv.open_container.manage');
  const [warehouseId, setWarehouseId] = useState('');
  const [opening, setOpening] = useState(false);
  const [discarding, setDiscarding] = useState<InvOpenContainerView | null>(null);

  const containers = useQuery({
    queryKey: [...stockKeys.containers(session?.tenantId), warehouseId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/open-containers', { params: { query: { warehouseId: warehouseId || undefined, status: 'Open' } } })),
  });

  const columns: DataTableColumn<InvOpenContainerView>[] = [
    {
      id: 'item',
      headerKey: 'inv:containers.columns.item',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.itemName}</span>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    { id: 'batch', headerKey: 'inv:containers.columns.batch', cell: (row) => <bdi dir="ltr">{row.batchNumber ?? '—'}</bdi> },
    { id: 'warehouse', headerKey: 'inv:containers.columns.warehouse', cell: (row) => warehouseName(warehouses.data?.items, row.warehouseId) },
    { id: 'opened', headerKey: 'inv:containers.columns.opened_at', cell: (row) => <JalaliDate value={row.openedAt} withTime /> },
    {
      id: 'remaining',
      headerKey: 'inv:containers.columns.remaining',
      cell: (row) => (
        <span data-testid={`container-remaining-${row.id}`}>
          {faDigits(String(Number(milliIn(row.remainingMilli, row.consumptionUnit, row.baseUnit, row.conversions).toFixed(3))))} {t(`inv:unit.${row.consumptionUnit}`)}
        </span>
      ),
    },
    {
      id: 'beyondUse',
      headerKey: 'inv:containers.columns.beyond_use',
      cell: (row) => {
        const hours = hoursLeft(row.beyondUseAt);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <JalaliDate value={row.beyondUseAt} withTime />
            <Chip size="small" color={hours < 0 ? 'error' : hours < 4 ? 'warning' : 'default'} label={hours < 0 ? t('inv:containers.expired') : t('inv:containers.hours_left', { hours: faDigits(String(hours)) })} />
          </Stack>
        );
      },
    },
    { id: 'status', headerKey: 'inv:containers.columns.status', cell: (row) => <StatusChip tone={row.status === 'Open' ? 'in_service' : 'done'} labelKey={`inv:container_status.${row.status}`} /> },
    {
      id: 'actions',
      headerKey: 'inv:containers.columns.actions',
      cell: (row) => (
        <Button size="small" color="error" disabled={!write.canWrite || row.status !== 'Open'} onClick={() => setDiscarding(row)} data-testid={`container-discard-${row.id}`}>
          {t('inv:containers.discard')}
        </Button>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_873}
      titleKey="inv:containers.title"
      subtitleKey="inv:containers.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} disabled={!write.canWrite} onClick={() => setOpening(true)} data-testid="container-open">
          {t('inv:containers.open')}
        </Button>
      }
      filters={<WarehouseSelect value={warehouseId} onChange={setWarehouseId} includeEmpty testId="containers-warehouse" />}
    >
      <WriteNotice write={write} />
      <DataTable
        captionKey="inv:containers.caption"
        columns={columns}
        rows={containers.data?.items}
        getRowId={(row) => row.id}
        isLoading={containers.isPending}
        error={containers.error}
        onRetry={() => void containers.refetch()}
        emptyTitleKey="inv:containers.empty"
      />
      {opening && <OpenContainerDialog defaultWarehouseId={warehouseId} onClose={() => setOpening(false)} />}
      {discarding && <DiscardDialog container={discarding} onClose={() => setDiscarding(null)} />}
    </ListPage>
  );
}

function OpenContainerDialog({ defaultWarehouseId, onClose }: { defaultWarehouseId: string; onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const items = useItemOptions();
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId);
  const [itemId, setItemId] = useState('');
  const [batchId, setBatchId] = useState('');
  const batches = useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, itemId, 'batches', true],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}/batches', { params: { path: { id: itemId }, query: { activeOnly: true } } })),
    enabled: Boolean(itemId),
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/open-containers', {
          body: { warehouseId, itemId, batchId: batchId || null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.containers(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog open screenId="SCR-STAFF-873-OPEN" titleKey="inv:containers.open_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(itemId)} isSubmitting={save.isPending} submitError={save.error}>
      <WarehouseSelect value={warehouseId} onChange={setWarehouseId} required testId="container-warehouse" />
      <TextField select required label={t('inv:containers.columns.item')} value={itemId} onChange={(event) => { setItemId(event.target.value); setBatchId(''); }} slotProps={{ htmlInput: { 'data-testid': 'container-item' } }}>
        {(items.data?.items ?? [])
          .filter((item) => item.isMultiUse)
          .map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
      </TextField>
      <TextField select label={t('inv:containers.columns.batch')} value={batchId} onChange={(event) => setBatchId(event.target.value)} helperText={t('inv:containers.fefo_help')} slotProps={{ htmlInput: { 'data-testid': 'container-batch' } }}>
        <MenuItem value="">{t('inv:documents.fefo')}</MenuItem>
        {(batches.data?.items ?? []).map((batch) => (
          <MenuItem key={batch.id} value={batch.id}>
            {batch.batchNumber}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

function DiscardDialog({ container, onClose }: { container: InvOpenContainerView; onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [reason, setReason] = useState<NonNullable<InvWasteReason>>('OpenedBeyondUse');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/open-containers/{id}:discard', {
          params: { path: { id: container.id } },
          body: { reason },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.containers(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-873-DISCARD" titleKey="inv:containers.discard_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={false} isSubmitting={save.isPending} submitError={save.error}>
      <TextField select required label={t('inv:documents.waste_reason')} value={reason} onChange={(event) => setReason(event.target.value as NonNullable<InvWasteReason>)} slotProps={{ htmlInput: { 'data-testid': 'container-discard-reason' } }}>
        {WASTE_REASONS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`inv:waste_reason.${value}`)}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}
